import { nullableArray, nullableOptional, toSearchParams } from '@shigo-ui/api-client';
import { DashboardTableStatusTone } from '@shigo-ui/dashboard-ui';
import { z } from 'zod';

import { dashboardT } from '../../../lib/messages';
import { targetingBuilderSchema } from '../../targeting/builder/model';

export enum PlacementType {
  Standard = 'STANDARD',
  AdUnit = 'ADUNIT',
}

export enum PlacementStatus {
  Active = 'ACTIVE',
  Inactive = 'INACTIVE',
  Archived = 'ARCHIVED',
}

export enum PlacementPricingType {
  None = 'NONE',
  RevenueShare = 'REVSHARE',
  Cpm = 'CPM',
}

export enum PlacementDeliveryType {
  App = 'APP',
  Ctv = 'CTV',
  Web = 'WEB',
}

export enum PlacementApprovalStatus {
  Pending = 'PENDING',
  Approved = 'APPROVED',
  Rejected = 'REJECTED',
  Submitted = 'SUBMITTED',
  NotApplicable = 'N_A',
}

export enum PlacementIvtType {
  Human = 'HUMAN',
}

export enum PlacementIvtIntegrationType {
  Pixel = 'PIXEL',
}

export enum PlacementSortProperty {
  Name = 'name',
  Type = 'type',
  Status = 'status',
  DeliveryType = 'deliveryType',
  BidFloor = 'bidFloor',
  Pricing = 'pricing',
  UpdatedOn = 'updatedOn',
}

export enum PlacementSortDirection {
  Asc = 'ASC',
  Desc = 'DESC',
}

export enum PlacementEditorTab {
  General = 'general',
  Pricing = 'pricing',
  Integrations = 'integrations',
  Targeting = 'targeting',
  Exclusions = 'exclusions',
  Delivery = 'delivery',
  AdUnit = 'ad-unit',
  XpMappings = 'xp-mappings',
}

export enum PlacementXpParameter {
  Xp1 = 'xp1',
  Xp2 = 'xp2',
  Xp3 = 'xp3',
  Xp4 = 'xp4',
  Xp5 = 'xp5',
  Xp6 = 'xp6',
  Xp7 = 'xp7',
  Xp8 = 'xp8',
  Xp9 = 'xp9',
  Xp10 = 'xp10',
}

const idSchema = z.union([z.string(), z.number()]).transform(String);
const nullableString = nullableOptional(z.string());
const nullableNumber = nullableOptional(z.number());
const nullableBoolean = nullableOptional(z.boolean());

export const placementPublisherSchema = z.looseObject({
  id: idSchema,
  name: z.string(),
  secondaryId: nullableString,
  status: nullableString,
  allProductsCost: z.looseObject({ costType: nullableString, value: nullableNumber }).nullish(),
  xpParametersMappings: nullableArray(
    z.looseObject({ value: z.string(), xpParameter: z.string() }),
  ),
});

export const placementTargetingSchema = targetingBuilderSchema.pick({
  id: true,
  name: true,
  status: true,
  geoLocations: true,
  deviceTypes: true,
});

export const placementDemandIntegrationSchema = z.looseObject({
  id: idSchema.optional(),
  demandIntegrationId: idSchema.optional(),
  demandIntegrationName: nullableString,
  placementId: nullableString,
  name: nullableString,
  dpDisplayName: nullableString,
  status: nullableString,
  demandPartner: z.looseObject({ displayName: nullableString }).nullish(),
  approvalStatus: z.enum(PlacementApprovalStatus).nullish(),
  publisherId: nullableString,
  siteId: nullableString,
  app_id: nullableString,
  zone_id: nullableString,
  tag_id: nullableString,
  display_manager: nullableString,
  triplelift_inventory_code: nullableString,
  openx_del_domain: nullableString,
  unruly_targeting_uuid: nullableString,
  openx_unit_id: nullableString,
  smart_ad_server_page_id: nullableString,
  smart_ad_server_network_id: nullableString,
  smart_ad_server_format_id: nullableString,
  duplication: nullableBoolean,
  duplicateDemandIntegration: nullableBoolean,
  revenueLast7Days: nullableNumber,
  revenuePct: nullableNumber,
  adRequestsLast7Days: nullableNumber,
  stats: z.looseObject({ global7daysRevenuePct: nullableNumber }).nullish(),
  sellerDomain: nullableString,
  schainAsi: nullableString,
  integrationType: nullableString,
});

export const placementAdUnitSchema = z.looseObject({
  name: nullableString,
  widgetName: nullableString,
  widgetId: nullableString,
  publisherDomains: nullableArray(z.looseObject({ publisherId: z.string(), domainId: z.string() })),
  video: nullableBoolean,
  adWidth: nullableNumber,
  adHeight: nullableNumber,
  devicePlacement: nullableString,
  s2s: nullableBoolean,
  bidder: nullableBoolean,
  unitConfig: z
    .looseObject({
      placementId: nullableString,
      viewIn: nullableString,
      viewOut: nullableString,
      adLoadDelay: nullableNumber,
      closeButton: nullableBoolean,
      adhesion: nullableBoolean,
      noFillHideUnit: nullableBoolean,
      idSystemIdentityLink: nullableBoolean,
      ibv: nullableBoolean,
      refreshRate: nullableNumber,
      maxRefresh: nullableNumber,
      weMakeSticky: nullableBoolean,
    })
    .nullish(),
});

export const placementSchema = z.looseObject({
  id: idSchema,
  name: z.string(),
  type: z.enum(PlacementType),
  status: z.enum(PlacementStatus),
  secondaryId: nullableString,
  deliveryType: z.enum(PlacementDeliveryType).nullish(),
  position: nullableString,
  bidFloor: nullableNumber,
  qpsLimit: nullableNumber,
  maxAdDuration: nullableNumber,
  shareOfVoice: nullableNumber,
  pricingType: z.enum(PlacementPricingType).nullish(),
  pricing: nullableNumber,
  cpm: nullableNumber,
  lineItemMargin: nullableNumber,
  notes: nullableString,
  debug: nullableBoolean,
  ignoreCompliance: nullableBoolean,
  passToDemandAppData: nullableBoolean,
  passToDemandAppStoreIds: nullableBoolean,
  blockBidsWithoutCatOrAdomain: nullableBoolean,
  ivtEnabled: nullableBoolean,
  ivtType: z.enum(PlacementIvtType).nullish(),
  ivtIntegrationType: z.enum(PlacementIvtIntegrationType).nullish(),
  ivtSamplingPercentage: nullableNumber,
  ivtGeneralRejectPercentage: nullableNumber,
  ivtSophisticatedRejectPercentage: nullableNumber,
  upstreamCpmCapEnabled: nullableBoolean,
  upstreamCpmCapMinCpm: nullableNumber,
  upstreamCpmCapMaxCpm: nullableNumber,
  rewriteVastEnabled: nullableBoolean,
  rewriteVastVersion: nullableNumber,
  schainIgnoreRequest: nullableBoolean,
  schainPreserveFirstOnly: nullableBoolean,
  poddingEnabled: nullableBoolean,
  maxPodSlotCount: nullableNumber,
  podFloor: nullableNumber,
  minPodDuration: nullableNumber,
  maxPodDuration: nullableNumber,
  deduplicateUniqueMediaFile: nullableBoolean,
  deduplicateUniqueAdvertiserDomain: nullableBoolean,
  deduplicateUniqueIABCategory: nullableBoolean,
  updatedOn: nullableString,
  createdOn: nullableString,
  publishers: nullableArray(placementPublisherSchema),
  publishersIds: nullableArray(idSchema),
  placementDemandIntegrations: nullableArray(placementDemandIntegrationSchema),
  demandIntegrationsCnt: z.number().nullish(),
  targetingList: nullableArray(placementTargetingSchema),
  targetingCnt: z.number().nullish(),
  adUnit: placementAdUnitSchema.nullish(),
  bcAvEnabled: nullableBoolean,
  bcVideoEnabled: nullableBoolean,
  bcAlwaysUseCache: nullableBoolean,
  stitcherEnabled: nullableBoolean,
  stAcceptPercentageOfFloor: nullableNumber,
  stMaxBids: nullableNumber,
  stMaxAvBids: nullableNumber,
  stMaxVideoBids: nullableNumber,
  stMaxBidsFromCache: nullableNumber,
  stIgnoreAdDuration: nullableBoolean,
  stPoddingFloor: nullableNumber,
  stFeeCpm: nullableNumber,
  stDedupeEnabled: nullableBoolean,
  stDedupeUniqueMediaFile: nullableBoolean,
  stDedupeUniqueAdvertiserDomain: nullableBoolean,
  stDedupeUniqueIABCategory: nullableBoolean,
  dmEnabled: nullableBoolean,
  dmShortTimeoutMs: nullableNumber,
  dmLongTimeoutMs: nullableNumber,
  exclusionsEnabled: nullableBoolean,
  advertiserDomains: nullableArray(z.string()),
  excludedCreativeIds: nullableArray(z.string()),
  iabCategoriesExcluded: nullableArray(z.string()),
  impressionTrackers: nullableArray(z.looseObject({ tracker: z.string(), sampleRate: z.number() })),
  xpParametersMappings: nullableArray(
    z.looseObject({ value: z.string(), xpParameter: z.string() }),
  ),
  overridePublisherXpParametersMapping: nullableBoolean,
});

export const placementsPageSchema = z.looseObject({
  content: z.array(placementSchema),
  totalElements: z.number(),
  totalPages: z.number().optional().default(0),
  number: z.number().optional().default(0),
  size: z.number().optional().default(0),
});

export const placementStatusCountsSchema = z.record(z.string(), z.number());
export const placementLookupPageSchema = z.looseObject({
  content: z.array(z.looseObject({ id: idSchema, name: z.string() })),
  totalElements: z.number(),
});
export const placementDemandIntegrationsPageSchema = z.looseObject({
  content: z.array(placementDemandIntegrationSchema),
  totalElements: z.number(),
});
export const placementTargetingsPageSchema = z.looseObject({
  content: z.array(placementTargetingSchema),
  totalElements: z.number(),
});

export type Placement = z.infer<typeof placementSchema>;
export type PlacementPublisher = z.infer<typeof placementPublisherSchema>;
export type PlacementTargeting = z.infer<typeof placementTargetingSchema>;
export type PlacementDemandIntegration = z.infer<typeof placementDemandIntegrationSchema>;
export type PlacementAdUnit = z.infer<typeof placementAdUnitSchema>;

export interface PlacementsFilter {
  searchTerm: string;
  statuses: PlacementStatus[];
  types: PlacementType[];
  demandIntegrationId?: string;
  publishersIds?: string[];
  page: number;
  size: number;
  sortProperty: PlacementSortProperty | null;
  sortDirection: PlacementSortDirection;
}

export interface PlacementLookupFilter {
  searchTerm: string;
  ids?: string[];
  page: number;
  size: number;
}

export interface PlacementStatusCountsFilter {
  searchTerm: string;
  types: PlacementType[];
  demandIntegrationId?: string;
  publishersIds?: string[];
}

export const placementPageSizes = [5, 10, 25, 50, 100] as const;
export const defaultPlacementsFilter: PlacementsFilter = {
  searchTerm: '',
  statuses: [PlacementStatus.Active],
  types: [],
  page: 0,
  size: 10,
  sortProperty: PlacementSortProperty.UpdatedOn,
  sortDirection: PlacementSortDirection.Desc,
};

export function serializePlacementsFilter(filter: PlacementsFilter) {
  return toSearchParams({
    searchTerm: filter.searchTerm,
    demandIntegrationId: filter.demandIntegrationId,
    status: filter.statuses,
    publishersIds: filter.publishersIds ?? [],
    type: filter.types,
    page: filter.page,
    size: filter.size,
    ...(filter.sortProperty
      ? { sortProperty: filter.sortProperty, sortDirection: filter.sortDirection }
      : {}),
  });
}

export function placementStatusCountsFilter(filter: PlacementsFilter): PlacementStatusCountsFilter {
  return {
    searchTerm: filter.searchTerm,
    types: filter.types,
    ...(filter.demandIntegrationId !== undefined
      ? { demandIntegrationId: filter.demandIntegrationId }
      : {}),
    ...(filter.publishersIds !== undefined ? { publishersIds: filter.publishersIds } : {}),
  };
}

export function serializePlacementStatusCountsFilter(filter: PlacementStatusCountsFilter) {
  return toSearchParams({
    searchTerm: filter.searchTerm,
    type: filter.types,
    demandIntegrationId: filter.demandIntegrationId,
    publishersIds: filter.publishersIds,
  });
}

export function serializePlacementLookupFilter(filter: PlacementLookupFilter) {
  return toSearchParams({
    searchTerm: filter.searchTerm.trim() || undefined,
    ids: filter.ids,
    page: filter.page,
    size: filter.size,
    sortProperty: 'name',
    sortDirection: PlacementSortDirection.Asc,
    status: PlacementStatus.Active,
  });
}

export function resolvePlacementStatusTone(status: PlacementStatus) {
  if (status === PlacementStatus.Active) return DashboardTableStatusTone.Success;
  if (status === PlacementStatus.Inactive) return DashboardTableStatusTone.Warning;
  return DashboardTableStatusTone.Neutral;
}

export function resolvePlacementStatusLabel(status: PlacementStatus) {
  const keys = {
    [PlacementStatus.Active]: 'placements.status.active',
    [PlacementStatus.Inactive]: 'placements.status.inactive',
    [PlacementStatus.Archived]: 'placements.status.archived',
  } as const;
  return dashboardT(keys[status]);
}

export function isPlacementSortProperty(value: string): value is PlacementSortProperty {
  return Object.values(PlacementSortProperty).includes(value as PlacementSortProperty);
}
